import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Gusto, PuntoVendita, StockDisponibileItem } from '../types';
import ApiService from '../services/api';
import { Trash2, Calendar, MapPin, AlertTriangle, Info } from 'lucide-react';
import moment from 'moment';
import { PageContainer, PageHeader, Textarea, Input, Select, Alert, Spinner, LoadingState } from '../components/ui';
import {
  SectionCard,
  FormSection,
  InputGroup,
  FieldLabel,
  GustiGrid,
  GustoCard,
  GustoHeader,
  GustoName,
  GustoColor,
  StockBadge,
  QuantityControl,
  QuantityButton,
  QuantityInput,
  SummaryCard,
  SummaryTitle,
  SummaryValue,
  SummaryUnit,
  SubmitButton,
  CenteredActions,
  InfoBanner,
  WarningBanner,
  EmptyState
} from '../components/rilevazione/ui';
import styled from 'styled-components';

// Dettagli specifici della pagina smaltimento
const SmaltimentoInfo = styled.div`
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  text-align: center;
  margin-top: var(--space-md);
`;

// ============================================================
// COMPONENTE PRINCIPALE
// ============================================================
interface SmaltimentoPageProps {
  onNavigate?: (page: string) => void;
}

const SmaltimentoPage: React.FC<SmaltimentoPageProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [data, setData] = useState(moment().format('YYYY-MM-DD'));
  const [puntoVenditaId, setPuntoVenditaId] = useState<number | null>(null);
  const [gusti, setGusti] = useState<Gusto[]>([]);
  const [puntiVendita, setPuntiVendita] = useState<PuntoVendita[]>([]);
  const [quantita, setQuantita] = useState<{ [key: number]: number }>({});
  const [stockDisponibile, setStockDisponibile] = useState<StockDisponibileItem[]>([]);
  const [note, setNote] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caricamento una tantum al mount
  }, []);

  useEffect(() => {
    if (data && puntoVenditaId) {
      loadStockDisponibile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ricarica solo al cambio di data/punto vendita; loadStockDisponibile è ricreata ad ogni render
  }, [data, puntoVenditaId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError('');

      const [gustiData, puntiVenditaData] = await Promise.all([
        ApiService.getGusti(),
        ApiService.getPuntiVendita()
      ]);

      setGusti(gustiData.filter(g => g.attivo));
      setPuntiVendita(puntiVenditaData);

      if (user?.punto_vendita_id) {
        setPuntoVenditaId(user.punto_vendita_id);
      } else if (puntiVenditaData.length === 1) {
        setPuntoVenditaId(puntiVenditaData[0].id);
      }
    } catch (error) {
      console.error('Errore caricamento dati:', error);
      setError('Errore durante il caricamento dei dati');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStockDisponibile = async () => {
    if (!data || !puntoVenditaId) return;

    try {
      const response = await ApiService.getStockDisponibile({ data, puntoVenditaId });
      setStockDisponibile(response.stock);
      setQuantita({});
    } catch (error: any) {
      console.error('Errore caricamento stock disponibile:', error);
      if (!error.response || error.response.status !== 404) {
        setError('Errore caricamento stock disponibile');
      }
      setStockDisponibile([]);
    }
  };

  const updateQuantita = (gustoId: number, value: number) => {
    const maxDisponibile = stockDisponibile.find(s => s.gusto_id === gustoId)?.quantita_disponibile || 0;
    setQuantita(prev => ({
      ...prev,
      [gustoId]: Math.max(0, Math.min(value, maxDisponibile))
    }));
  };

  const incrementQuantita = (gustoId: number, delta: number) => {
    const maxDisponibile = stockDisponibile.find(s => s.gusto_id === gustoId)?.quantita_disponibile || 0;
    setQuantita(prev => {
      const current = prev[gustoId] || 0;
      return { ...prev, [gustoId]: Math.max(0, Math.min(current + delta, maxDisponibile)) };
    });
  };

  const getTotaleKg = () => {
    return Object.values(quantita).reduce((total, qty) => total + qty, 0);
  };

  const getGustiSelezionati = () => {
    return Object.entries(quantita)
      .filter(([_, qty]) => qty > 0)
      .map(([gustoId, qty]) => ({
        gusto_id: parseInt(gustoId),
        quantita_kg: qty
      }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!puntoVenditaId) {
      setError('Seleziona un punto vendita');
      return;
    }

    const gustiSelezionati = getGustiSelezionati();

    if (gustiSelezionati.length === 0) {
      setError('Seleziona almeno un gusto da smaltire');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await ApiService.saveSmaltimento({
        data,
        puntoVenditaId,
        gusti: gustiSelezionati,
        note
      });

      setSuccess(`Smaltimento registrato. Totale: ${response.kg_totale.toFixed(2)} kg`);

      // Ricarica i dati dopo 2 secondi
      setTimeout(() => {
        loadStockDisponibile();
      }, 2000);
    } catch (error: any) {
      console.error('Errore salvataggio smaltimento:', error);
      setError(error.response?.data?.error || 'Errore durante il salvataggio');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState />
      </PageContainer>
    );
  }

  const totaleKg = getTotaleKg();
  const numGusti = getGustiSelezionati().length;

  return (
    <PageContainer>
      <PageHeader title="Smaltimento Gelato" icon={<Trash2 />} />

      <WarningBanner>
        <AlertTriangle size={24} />
        <div>
          <strong>Registra il gelato da smaltire/buttare.</strong>
          <br />
          Puoi scartare solo il gelato disponibile (stock cumulativo).
        </div>
      </WarningBanner>

      {error && <Alert $variant="error">{error}</Alert>}
      {success && <Alert $variant="success">{success}</Alert>}

      <form onSubmit={handleSubmit}>
        <SectionCard>
          <FormSection>
            <InputGroup>
              <FieldLabel>
                <Calendar size={20} />
                Data
              </FieldLabel>
              <Input
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                required
              />
            </InputGroup>

            <InputGroup>
              <FieldLabel>
                <MapPin size={20} />
                Punto Vendita
              </FieldLabel>
              <Select
                value={puntoVenditaId || ''}
                onChange={(e) => setPuntoVenditaId(parseInt(e.target.value))}
                required
              >
                <option value="">Seleziona punto vendita</option>
                {puntiVendita.map(pv => (
                  <option key={pv.id} value={pv.id}>{pv.nome}</option>
                ))}
              </Select>
            </InputGroup>
          </FormSection>
        </SectionCard>

        {stockDisponibile.length === 0 ? (
          <SectionCard>
            <EmptyState>
              <Trash2 size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <div>Nessun gelato disponibile per smaltimento in questa data.</div>
              <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                Verifica che ci sia produzione o giacenza precedente.
              </div>
            </EmptyState>
          </SectionCard>
        ) : (
          <SectionCard>
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Stock Disponibile</h2>
            <InfoBanner style={{ fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              <Info size={20} />
              <div>Mostra il gelato disponibile per smaltimento (giacenza ieri + produzione oggi).</div>
            </InfoBanner>
            <GustiGrid>
              {stockDisponibile.map(stock => {
                const gusto = gusti.find(g => g.id === stock.gusto_id);
                const gustoId = gusto?.id ?? stock.gusto_id;
                const gustoNome = gusto?.nome ?? stock.gusto_nome;
                const gustoColore = gusto?.colore ?? stock.gusto_colore;
                const gustoDisattivato = !gusto;
                const qty = quantita[gustoId] || 0;
                const isSelected = qty > 0;

                return (
                  <GustoCard key={gustoId} $tone="error" className={isSelected ? 'selected' : ''}>
                    <GustoHeader>
                      <GustoName>
                        <GustoColor color={gustoColore} />
                        {gustoNome}{gustoDisattivato ? ' (disattivato)' : ''}
                      </GustoName>
                      <StockBadge>
                        {stock.quantita_disponibile.toFixed(2)} kg
                      </StockBadge>
                    </GustoHeader>

                    <QuantityControl>
                      <QuantityButton
                        type="button"
                        $tone="error"
                        onClick={() => incrementQuantita(gustoId, -0.5)}
                        disabled={qty === 0}
                      >
                        −
                      </QuantityButton>
                      <QuantityInput
                        type="number"
                        step="0.1"
                        min="0"
                        max={stock.quantita_disponibile}
                        value={qty || ''}
                        onChange={(e) => updateQuantita(gustoId, parseFloat(e.target.value) || 0)}
                        placeholder="0"
                      />
                      <QuantityButton
                        type="button"
                        $tone="error"
                        onClick={() => incrementQuantita(gustoId, 0.5)}
                        disabled={qty >= stock.quantita_disponibile}
                      >
                        +
                      </QuantityButton>
                    </QuantityControl>

                    {qty > 0 && (
                      <SmaltimentoInfo>
                        Da smaltire: {qty.toFixed(2)} kg
                      </SmaltimentoInfo>
                    )}
                  </GustoCard>
                );
              })}
            </GustiGrid>
          </SectionCard>
        )}

        <SectionCard>
          <InputGroup>
            <FieldLabel>Note / Motivo smaltimento</FieldLabel>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Es: Gelato scaduto, contaminato, difettoso..."
              maxLength={500}
            />
          </InputGroup>
        </SectionCard>

        {numGusti > 0 && (
          <SummaryCard $tone="error">
            <SummaryTitle>Totale Smaltimento</SummaryTitle>
            <SummaryValue>
              {totaleKg.toFixed(2)} <SummaryUnit>kg</SummaryUnit>
            </SummaryValue>
            <div>{numGusti} gusti da smaltire</div>
          </SummaryCard>
        )}

        <CenteredActions>
          <SubmitButton type="submit" $tone="error" disabled={isSubmitting || numGusti === 0}>
            {isSubmitting ? (
              <>
                <Spinner $size={20} />
                Salvataggio...
              </>
            ) : (
              <>
                <Trash2 size={20} />
                Registra Smaltimento
              </>
            )}
          </SubmitButton>
        </CenteredActions>
      </form>
    </PageContainer>
  );
};

export default SmaltimentoPage;
